/**
 * Session Tracker
 * Tracks user sessions, journey, and generates session analytics
 */

import { sendNewRelicEvent, setNewRelicAttribute } from '../newrelic';

import { ANALYTICS_EVENTS, HEARTBEAT_INTERVAL_MS, PAGE_TO_FEATURE } from './constants';

// Internal state
let sessionId = null;
let sessionStartTime = null;
let pageSequence = [];
let featuresUsed = new Set();
let heartbeatInterval = null;
let currentPath = null;
let isInitialized = false;
let beforeUnloadHandler = null;

/**
 * Generate unique session ID
 */
const generateSessionId = () => {
  const timestamp = Date.now().toString(36);
  const randomPart = Math.random().toString(36).substring(2, 9);
  return `${timestamp}-${randomPart}`;
};

/**
 * Get feature from path
 */
const getFeatureFromPath = (path) => {
  if (PAGE_TO_FEATURE[path]) {
    return PAGE_TO_FEATURE[path];
  }
  const matchingKey = Object.keys(PAGE_TO_FEATURE).find((key) => path.startsWith(key));
  return matchingKey ? PAGE_TO_FEATURE[matchingKey] : null;
};

/**
 * Send heartbeat event
 */
const sendHeartbeat = () => {
  if (!sessionId) return;

  const sessionDurationSeconds = Math.round((Date.now() - sessionStartTime) / 1000);

  sendNewRelicEvent(ANALYTICS_EVENTS.SESSION_HEARTBEAT, {
    currentPage: currentPath,
    featuresUsed: Array.from(featuresUsed),
    pagesVisited: pageSequence.length,
    sessionDurationSeconds,
    sessionId,
  });
};

/**
 * Start heartbeat
 */
const startHeartbeat = () => {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
  }
  heartbeatInterval = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);
};

/**
 * Stop heartbeat
 */
const stopHeartbeat = () => {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
};

/**
 * Start a new session
 */
export const startSession = () => {
  sessionId = generateSessionId();
  sessionStartTime = Date.now();
  pageSequence = [];
  featuresUsed = new Set();

  // Set session ID as a persistent attribute
  setNewRelicAttribute('sessionId', sessionId);

  sendNewRelicEvent(ANALYTICS_EVENTS.SESSION_STARTED, {
    language: navigator.language,
    platform: navigator.platform,
    referrer: document.referrer,
    sessionId,
    startTimestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
  });

  startHeartbeat();

  return sessionId;
};

/**
 * End the current session
 */
export const endSession = () => {
  if (!sessionId) return;

  stopHeartbeat();

  const sessionDurationSeconds = Math.round((Date.now() - sessionStartTime) / 1000);

  sendNewRelicEvent(ANALYTICS_EVENTS.SESSION_ENDED, {
    endTimestamp: new Date().toISOString(),
    featuresUsed: Array.from(featuresUsed),
    featuresUsedCount: featuresUsed.size,
    pagesVisited: pageSequence.length,
    sessionId,
    totalDurationSeconds: sessionDurationSeconds,
    uniquePagesVisited: new Set(pageSequence).size,
  });

  // Reset state
  sessionId = null;
  sessionStartTime = null;
  pageSequence = [];
  featuresUsed = new Set();
};

/**
 * Track a navigation step in the user journey
 * @param {string} toPath - The destination path
 * @param {string} fromPath - The source path (optional)
 */
export const trackJourneyStep = (toPath, fromPath = null) => {
  if (!sessionId) {
    startSession();
  }

  const previousPath = fromPath || currentPath;
  currentPath = toPath;

  // Add to page sequence
  pageSequence.push(toPath);

  // Track feature usage
  const feature = getFeatureFromPath(toPath);
  if (feature) {
    featuresUsed.add(feature);
  }

  sendNewRelicEvent(ANALYTICS_EVENTS.USER_JOURNEY_STEP, {
    featureName: feature,
    fromPage: previousPath,
    sessionId,
    stepNumber: pageSequence.length,
    timestamp: new Date().toISOString(),
    toPage: toPath,
  });
};

/**
 * Track feature usage
 * @param {string} featureName - The feature being used
 * @param {string} action - The action performed
 * @param {object} context - Additional context
 */
export const trackFeatureUsage = (featureName, action, context = {}) => {
  if (!sessionId) {
    startSession();
  }

  featuresUsed.add(featureName);

  sendNewRelicEvent(ANALYTICS_EVENTS.FEATURE_USED, {
    action,
    featureName,
    sessionId,
    timestamp: new Date().toISOString(),
    ...context,
  });
};

/**
 * Get current session info
 */
export const getSessionInfo = () => ({
  currentPath,
  featuresUsed: Array.from(featuresUsed),
  pagesVisited: pageSequence.length,
  sessionDurationSeconds: sessionStartTime ? Math.round((Date.now() - sessionStartTime) / 1000) : 0,
  sessionId,
});

/**
 * Get session ID
 */
export const getSessionId = () => sessionId;

/**
 * Initialize session tracker
 */
export const initSessionTracker = () => {
  if (typeof window === 'undefined') return;

  // Prevent multiple initializations
  if (isInitialized) return;
  isInitialized = true;

  // Start session if not already started
  if (!sessionId) {
    startSession();
  }

  // Create named handler for proper cleanup
  beforeUnloadHandler = () => {
    endSession();
  };

  // End session on page unload
  window.addEventListener('beforeunload', beforeUnloadHandler);
};

/**
 * Cleanup session tracker
 */
export const cleanupSessionTracker = () => {
  // Remove event listener if it was added
  if (beforeUnloadHandler && typeof window !== 'undefined') {
    window.removeEventListener('beforeunload', beforeUnloadHandler);
    beforeUnloadHandler = null;
  }

  isInitialized = false;
  endSession();
};

export default {
  cleanupSessionTracker,
  endSession,
  getSessionId,
  getSessionInfo,
  initSessionTracker,
  startSession,
  trackFeatureUsage,
  trackJourneyStep,
};
